<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div>
  <b-loading :is-full-page="false" :active="false" />
  <div class="page-wrapper content-wrapper">
    <div class="panel">
      <div style="width: 100%;">
        <div class="content-title-wrapper">
          <label class="hierarchy-label">{{ $t('profile') }}</label>
        </div>
        <div class="account-form-wrapper">
          <form @submit.prevent="saveProfile()" data-vv-scope="profile">
            <b-field :label="$t('id')" horizontal v-if="currentUser.isDeveloper">
              {{currentUser.id}}
            </b-field>

            <b-field :label="$t('username')" horizontal>
              <b-input :value="currentUser.username" disabled />
            </b-field>

            <b-field :label="$t('role')" horizontal>
              <span class="tag" :class="role.class">{{$t(role.label)}}</span>
            </b-field>

            <b-field
              :label="$t('firstname')"
              horizontal
              :type="{'is-danger': errors.has('profile.firstname')}"
              :message="errors.first('profile.firstname')"
            >
              <b-input v-model="updatedUser.firstname" name="firstname" v-validate="'required'" />
            </b-field>

            <b-field
              :label="$t('lastname')"
              horizontal
              :type="{'is-danger': errors.has('profile.lastname')}"
              :message="errors.first('profile.lastname')"
            >
              <b-input v-model="updatedUser.lastname" name="lastname" v-validate="'required'" />
            </b-field>

            <b-field
              :label="$t('email')"
              horizontal
              :type="{'is-danger': errors.has('profile.email')}"
              :message="errors.first('profile.email')"
            >
              <b-input v-model="updatedUser.email" name="email" v-validate="'required|email'" />
            </b-field>

            <b-field :label="$t('language')" horizontal>
              <b-select v-model="updatedUser.language">
                <option v-for="{value, name} in languages" :key="value" :value="value">
                  {{name}}
                </option>
              </b-select>
            </b-field>

            <!-- <b-field :label="$t('developer-mode')" horizontal>
              <b-switch v-model="updatedUser.isDeveloper" class="switch">
                <template v-if="updatedUser.isDeveloper">{{$t('yes')}}</template>
                <template v-else>{{$t('no')}}</template>
              </b-switch>
            </b-field> -->

            <hr class="divider">

            <input id="password" class="hidden" type="password"> <!-- HACK: fake field to prevent autofill -->

            <b-message v-if="currentUser.passwordExpired" type="is-danger" has-icon icon-size="is-small">
              {{$t('password-expired-info-message')}}
            </b-message>

            <b-field
              v-else
              :label="$t('password-current')"
              horizontal
              :type="typeCurrentPassword"
              :message="messageCurrentPassword"
            >
              <b-input type="password" v-model="currentPassword" :loading="isCheckingPassword" />
            </b-field>

            <b-field
              :label="$t('password-new')"
              horizontal
              :type="{'is-danger': errors.has('profile.newPassword')}"
              :message="errors.first('profile.newPassword')"
            >
              <b-input
                type="password"
                v-model="newPassword"
                name="newPassword"
                v-validate="'password_pattern'"
                :disabled="newPasswordDisabled"
              />
            </b-field>

            <b-field
              :label="$t('password-confirm')"
              horizontal
              :type="{'is-danger': errors.has('profile.confirmPassword')}"
              :message="errors.first('profile.confirmPassword')"
            >
              <b-input
                type="password"
                v-model="confirmPassword"
                name="confirmPassword"
                v-validate="{is: newPassword}"
                :disabled="newPasswordDisabled"
              />
            </b-field>

            <b-field grouped position="is-right">
              <div class="control">
                <button class="button is-link" :disabled="errors.any('profile') || (currentPassword && !correctPassword) || (!newPasswordDisabled && newPassword !== confirmPassword)">
                  {{$t('button-save')}}
                </button>
              </div>
            </b-field>
          </form>
          <div v-if="this.currentUser.adminByNow && this.currentUser.username === 'admin'">
            <hr class="divider">
            <div>
              <label class="hierarchy-subtitle">{{ $t('image-import-setting') }}</label>
            </div>
            <div>
              <b-field :label="$t('default-case')" horizontal>
                <b-select v-model="defaultCaseId">
                  <option :value="null">{{ $t('none') }}</option>
                  <option v-for="{id, name} in projects" :key="id" :value="id">{{name}}</option>
                </b-select>
              </b-field>

              <b-field grouped position="is-right">
                <div class="control">
                  <button class="button is-link" @click="updatePreference">
                    {{$t('button-save')}}
                  </button>
                </div>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="panel" v-if="currentUser.isDeveloper"> <!-- QUESTION: remove ? or only show for technical users ? -->
      <div style="width: 100%;">
        <div class="content-title-wrapper">
          <label class="hierarchy-label">{{ $t('api-keys') }}</label>
        </div>
        <div class="account-form-wrapper">
        <b-field :label="$t('public-key')" horizontal>
          <b-field>
            <b-input :value="currentUser.publicKey" readonly expanded />
            <p class="control">
              <button class="button" @click="copy(currentUser.publicKey)">
                <span class="icon"><i class="far fa-clipboard"></i></span>
                <span>{{$t('button-copy')}}</span>
              </button>
            </p>
          </b-field>
        </b-field>

        <b-field :label="$t('private-key')" horizontal>
          <b-field>
            <b-input :value="currentUser.privateKey" readonly expanded />
            <p class="control">
              <button class="button" @click="copy(currentUser.privateKey)">
                <span class="icon"><i class="far fa-clipboard"></i></span>
                <span>{{$t('button-copy')}}</span>
              </button>
            </p>
          </b-field>
        </b-field>

        <b-field grouped position="is-right">
          <div class="control">
            <button class="button is-link" @click="regenerateKeys()">{{$t('regenerate-api-keys')}}</button>
          </div>
        </b-field>
        </div>
      </div>
    </div>

    <div class="logout-section">
      <button class="button is-danger" @click="logout()">
        {{$t('logout')}}
      </button>
    </div>
  </div>
</div>
</template>

<script>
import { Validator } from 'vee-validate';
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';
import _ from 'lodash';
import {User, Cytomine, ProjectCollection, PreferenceCollection, Preference} from 'cytomine-client';
import {rolesMapping} from '@/utils/role-utils';
import copyToClipboard from 'copy-to-clipboard';

export default {
  // eslint-disable-next-line
  name: 'Account',
  $_veeValidate: {validator: 'new'},
  mixins: [changeLanguageMixin],
  data() {
    return {
      updatedUser: this.$store.state.currentUser.user.clone(),
      currentPassword: '',
      isCheckingPassword: false,
      correctPassword: false,
      newPassword: '',
      confirmPassword: '',
      languages: [
        {value: 'EN', name:'English'},
        {value: 'FR', name:'Français'},
        {value: 'ES', name:'Español'},
        {value: 'NL', name:'Nederlands'}
      ],
      projects: [],
      preference: {},
      defaultCaseId: null,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    role() {
      if(this.currentUser.superAdminByNow) {
        return rolesMapping['ROLE_SUPER_ADMIN'];
      }
      else if(this.currentUser.adminByNow) {
        return rolesMapping['ROLE_ADMIN'];
      }
      else if(this.currentUser.guestByNow) {
        return rolesMapping['ROLE_GUEST'];
      }
      else {
        return rolesMapping['ROLE_USER'];
      }
    },
    newPasswordDisabled() {
      return !this.correctPassword && !this.currentUser.passwordExpired;
    },
    messageCurrentPassword() {
      if(!this.currentPassword || this.isCheckingPassword) {
        return;
      }
      return this.correctPassword ? '' : this.$t('invalid-password');
    },
    typeCurrentPassword() {
      if(!this.currentPassword || this.isCheckingPassword) {
        return;
      }
      return this.correctPassword ? 'is-success' : 'is-danger';
    }
  },
  watch: {
    currentPassword() {
      this.isCheckingPassword = true;
      this.checkPassword();
    }
  },
  methods: {
    async saveProfile() {
      let result = await this.$validator.validateAll('profile');
      if(!result) {
        return;
      }

      // Check if password change is requested
      if(this.newPassword && (!this.correctPassword && !this.currentUser.passwordExpired)) {
        return;
      }

      try {
        // Update user details
        let userToUpdate = this.updatedUser.clone();
        
        // Add password if it's being changed
        if(this.newPassword) {
          userToUpdate.password = this.newPassword;
        }

        await this.$store.dispatch('currentUser/updateUser', userToUpdate);
        this.changeLanguage(this.currentUser.language);
        
        // Reset password fields if password was changed
        if(this.newPassword) {
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.$validator.reset({scope: 'profile'});
          this.$notify({type: 'success', text: this.$t('notif-success-user-details-and-password-saved')});
        }
        else {
          this.$notify({type: 'success', text: this.$t('notif-success-user-details-saved')});
        }
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-user-details-not-saved')});
      }
    },

    checkPassword: _.debounce(async function() {
      this.correctPassword = await User.checkCurrentPassword(this.currentPassword);
      if(!this.correctPassword) {
        this.newPassword = '';
        this.confirmPassword = '';
        this.$validator.reset({scope: 'profile'});
      }
      this.isCheckingPassword = false;
    }, 500),

    copy(value) {
      copyToClipboard(value);
      this.$notify({type: 'success', text: this.$t('notif-success-key-copied')});
    },

    async regenerateKeys() {
      try {
        await this.$store.dispatch('currentUser/regenerateKeys');
        this.$notify({type: 'success', text: this.$t('notif-success-keys-regenerated')});
      }
      catch(err) {
        this.$notify({type: 'error', text: this.$t('notif-error-keys-not-regenerated')});
      }
    },

    async logout() {
      try {
        if (this.currentUser.adminByNow)
          await this.$store.dispatch('currentUser/closeAdminSession');
        await Cytomine.instance.logout();
        this.$store.dispatch('logout');
        this.changeLanguage();
        this.$router.push('/');
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-logout')});
      }
    },

    // 사용자의 preference 정보를 가져오기
    async fetchPreference() {
      this.preference = await PreferenceCollection.fetchByUser(this.currentUser.id);
    },
    // 사용자가 접근 가능한 project (id, name) 목록을 가져오기
    async fetchProject() {
      const result = await ProjectCollection.fetchAll();
      this.projects = result._data.map(p => ({
        id: p.id,
        name: p.name
      }));
    },
    async updatePreference() {
      console.log('>>>', this.currentUser.username);
      try {
        this.preference.defaultCaseId = this.defaultCaseId;
        this.preference.userId = this.currentUser.id;
        await new Preference(this.preference).saveByUser();
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-preference-update')
        });
      }
      catch(error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-preference-update')
        });
      }
    }
  },
  async created() {
    Validator.extend('password_pattern', {
      getMessage: () => this.$t('invalid-password-pattern'),
      validate: value => /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d\W]{8,32}$/.test(value) && !/\s/.test(value)
    });

    await Promise.all([
      await this.fetchProject(),
      await this.fetchPreference(),
    ]);

    this.defaultCaseId = this.preference.defaultCase;
  },
};
</script>

<style scoped lang="scss">
.page-wrapper {
  vertical-align: middle !important;
  height: 100%;
}

.content-wrapper {
  padding: 0 2.5%;
  position: relative;
  min-height: 100%;
  overflow: hidden;
}

.content-title-wrapper {
  height: 75px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.panel {
  display: flex;
  flex-direction: row;
}

.hierarchy-label {
  margin: 18px 10px;
  font-size: 1.3rem;
  color: #4A4847;
}

.hierarchy-subtitle {
  line-height: 3.5rem;
  margin: 0px 20px;
  font-size: 1.3rem;
  color: #4A4847;
}

.account-form-wrapper {
  padding: 20px 30px;
}

.table-tool-icon-bar {
  height: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.icon-style {
  cursor: pointer;
  margin-right: 10px;
  padding: 10px;
}

.fas {
  margin-right: 0.3em;
}

input.hidden {
  display: none;
}

.logout-section {
  display: flex;
  justify-content: flex-end;
}

.divider {
  margin: 2em 0;
  border: none;
  border-top: 1px solid #dbdbdb;
}

.display-flex-row {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
</style>
